import { useState } from 'react';

import {
  convertEpochToHumanReadable,
  convertHumanReadableToEpoch,
} from '../../modules/tools/converters';

const MODES = [
  {
    id: 'epoch-to-date',
    label: 'Epoch → Data',
    description: 'Informe segundos ou milissegundos desde 01/01/1970.',
    placeholder: '1704067200',
  },
  {
    id: 'date-to-epoch',
    label: 'Data → Epoch',
    description: 'Aceita formatos ISO ou locais reconhecidos pelo JavaScript.',
    placeholder: '2024-01-01T00:00:00',
  },
];

const EpochConverterTool = () => {
  const [mode, setMode] = useState(MODES[0].id);
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');

  const currentMode = MODES.find((item) => item.id === mode) ?? MODES[0];

  const handleConvert = () => {
    setError(null);
    setCopyFeedback('');

    try {
      const result =
        mode === 'epoch-to-date'
          ? convertEpochToHumanReadable(input)
          : convertHumanReadableToEpoch(input);
      setOutput(result);
    } catch (err) {
      setOutput('');
      setError(err.message);
    }
  };

  const handleClear = () => {
    setInput('');
    setOutput('');
    setError(null);
    setCopyFeedback('');
  };

  const handleCopy = async () => {
    if (!output) {
      return;
    }

    try {
      if (!navigator?.clipboard) {
        throw new Error('API de área de transferência indisponível.');
      }

      await navigator.clipboard.writeText(output);
      setCopyFeedback('Resultado copiado!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback(`Falha ao copiar: ${err.message}`);
    }
  };

  const handleExport = () => {
    if (!output || typeof window === 'undefined') {
      return;
    }

    const blob = new Blob([output], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${mode}-resultado.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {MODES.map((item) => {
          const isActive = item.id === mode;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                setMode(item.id);
                setInput('');
                setOutput('');
                setError(null);
                setCopyFeedback('');
              }}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                isActive
                  ? 'bg-brand-primary text-white shadow'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-slate-500 dark:text-slate-400">{currentMode.description}</p>

      <input
        type="text"
        value={input}
        onChange={(event) => setInput(event.target.value)}
        placeholder={currentMode.placeholder}
        className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 transition focus:border-brand-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
      />

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={handleConvert}
          className="rounded-lg bg-brand-primary px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-brand-primary/90"
        >
          Converter
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Limpar
        </button>
        <button
          type="button"
          onClick={handleCopy}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          disabled={!output}
        >
          Copiar
        </button>
        <button
          type="button"
          onClick={handleExport}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          disabled={!output}
        >
          Exportar
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-600 dark:border-rose-500/50 dark:bg-rose-500/10 dark:text-rose-300">
          {error}
        </div>
      )}

      {copyFeedback && (
        <div className="text-sm font-medium text-brand-primary">{copyFeedback}</div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          Resultado
        </h3>
        <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-slate-950/90 px-4 py-3 text-sm text-slate-100 dark:border-slate-700 dark:bg-slate-900">
          {output || 'Converta um valor para visualizar o resultado.'}
        </pre>
      </div>
    </div>
  );
};

export default EpochConverterTool;
